/**
 * The application logic, decoupled from the browser so it can be integration-tested. Every
 * environment dependency — the store, the transport, GPS, the clock, the download sink —
 * arrives through {@link Platform}, so a test drives the whole connect → read → record →
 * download pipeline with fakes (REQ-NFR-4). main.ts builds the real Platform from globals.
 */
import type { View, ViewHandlers, Mode } from './ui/view';
import { createRecorder, type DeviceInfo, type LogRecord, type Recorder, type Clock } from './log';
import type { Store } from './store';
import { createLineAssembler } from './framing';
import { openCh340, type Ch340Port, type Ch340Error } from './ch340';
import { createTelemetry, type Telemetry } from './telemetry';
import { toNdjsonLine, downloadName } from './ndjson';
import {
  buildCfg,
  BANDS,
  bandOf,
  clampPwr,
  clampPwrInLine,
  mergeCfgLine,
  setDelayCommand,
  setPwrmaxCommand,
  type CfgState,
} from './cfg';
import type { Upstream } from './outbox';
import type { RemoteOutcome } from './remote';
import { ok, err, type Result } from './result';

export type WakeHandle = { release(): Promise<void> };

export type Platform = {
  store: Store;
  build: string;
  ua: string;
  clock: Clock;
  uuid(): string;
  now(): Date;
  /** Show the device chooser; null if cancelled or WebUSB is unavailable. */
  requestDevice(): Promise<USBDevice | null>;
  /** Open the CH340 on a chosen device (defaults to openCh340; injected for tests). */
  openPort(device: USBDevice): Promise<Result<Ch340Port, Ch340Error>>;
  /** Start a position watch, or a no-op if geolocation is absent. */
  watchGps(onFix: (p: GeolocationPosition) => void, onError: () => void): void;
  /** Register the WebUSB disconnect listener. */
  onDisconnect(cb: () => void): void;
  /** Fire when the page returns to the foreground (visibilitychange → visible). */
  onVisible(cb: () => void): void;
  acquireWakeLock(): Promise<WakeHandle | null>;
  /** Save the assembled log to a file (Blob + anchor in the browser; captured in tests). */
  save(parts: string[], filename: string): void;
  /**
   * Where board lines go for the laptop's live copy. A **no-op sink** when there is no relay —
   * not an optional field, so there is no `if (remote)` branch anywhere in the read loop or the
   * send path, and REQ-REM-1 is a property of the wiring rather than of a condition (02 §3.6).
   */
  upstream: Upstream;
};

const BAUD = 115200;

/**
 * The page's verb allowlist (08 §3.6). It matches the **first whitespace-separated token** and
 * nothing else — no keys, no values, no ranges.
 *
 * The page keeps a list at all even though the laptop already blocks `seal` and `dfu`, because the
 * relay address arrives in `location.hash` and the page trusts it: a substituted address means a
 * relay the laptop's blocklist never sees. This is the page's only defence against that, and it is
 * short enough not to drift.
 *
 * `cfg?` is a separate entry on purpose — it arrives as `argv[0]` in its own right (07 §5.1), and a
 * list holding only `cfg` would refuse every config query, which is the one command the near side
 * uses to confirm a remote `cfg` landed.
 */
const REMOTE_VERBS = new Set(['cfg', 'cfg?', 'per', 'tof', 'stop', 'ver', 'led']);

/**
 * Max content bytes in one console line. `proto.h:22` sets `LINE_CAP = 127u` and a longer line is
 * dropped by the board's reader, answered `err line too long` (contract 08 §3.6).
 *
 * Exported so the tests assert against this and not against a fourth copy of the number. The
 * authority is the contract, not this file — if it moves, it moves there first.
 */
export const LINE_CAP = 127;

export class App {
  readonly view: View;
  private mode: Mode = 'console';
  private readonly session: string;
  private readonly recorder: Recorder;
  private count = 0;
  // The session starts logging on the first successful connect, when the meta record is
  // written. Nothing is recorded before that — so meta is always the session's first
  // record (seq 0), and a session that never connects produces no records (log-format §3).
  private started = false;
  // Seeded at pwr 0, not 14 (07 §9.1). The old seed mirrored a boot default the firmware no
  // longer has, and `buildCfg` sends every key — so one tap of APPLY put the far board at
  // +14 dBm, which is over the chip's +10 dBm absolute-max RF input beside a cabled pair.
  private cfg: CfgState = { freq: BANDS['868'].freq, sf: 10, bw: 125, cr: '4/5', pwr: 0, sync: 0x12 };
  /** Exactly one saved state, for {@link undoLastConfig}. */
  private prevCfg: CfgState | null = null;
  private tofdelay = 0;
  // 0 dBm, matching the firmware's own boot value (PROTOCOL.md §2.2). The ceiling is a
  // session-level setting the operator raises deliberately, so the safe end is the default.
  private pwrmax = 0;
  private gpsEnabled = true;
  private lastFixMono: number | null = null;
  private lastAcc: number | null = null;
  private port: Ch340Port | null = null;
  private readonly assembler = createLineAssembler();
  private readonly telemetry: Telemetry = createTelemetry();
  private wake: WakeHandle | null = null;
  /** The active read loop, so tests can await it draining. */
  private reading: Promise<void> = Promise.resolve();

  constructor(makeView: (h: ViewHandlers) => View, private readonly p: Platform) {
    this.session = p.uuid();
    this.recorder = createRecorder(this.session, p.build, p.ua, p.clock);
    this.view = makeView(this.handlers());
  }

  private handlers(): ViewHandlers {
    return {
      onConnect: () => void this.connect(),
      onSend: (t, src) => void this.send(t, src),
      onToggleTheme: () => {}, // theme is owned by main.ts (localStorage)
      onToggleMode: (m) => this.setMode(m),
      onOpenSessions: () => void this.openSessions(),
      onOpenConfig: () => this.openConfig(),
      onApplyConfig: (cfg) => void this.applyConfig(cfg),
      onSetTofdelay: (v) => void this.setTofdelay(v),
      onSetPwrmax: (v) => void this.setPwrmax(v),
      onUndoConfig: () => void this.undoLastConfig(),
      onToggleGps: (enabled) => this.setGpsLogging(enabled),
      onDownload: (s) => void this.download(s),
      onDelete: (s) => void this.p.store.remove(s),
    };
  }

  openConfig(): void {
    this.view.openConfig({
      cfg: this.cfg,
      tofdelay: this.tofdelay,
      pwrmax: this.pwrmax,
      gpsEnabled: this.gpsEnabled,
      connected: this.port !== null,
    });
  }

  /**
   * The one place a full `cfg` is applied. The clamp lives HERE and not in the Config sheet
   * (07 §9.2): the sheet was the only caller until the remote console arrived, and a guard that
   * only one caller passes through is decoration. `ui/config.ts` keeps its own call so the field
   * visibly snaps when the operator types 30 — that is feedback, and deleting it would weaken
   * nothing.
   */
  async applyConfig(cfg: CfgState): Promise<void> {
    const safe = { ...cfg, pwr: clampPwr(cfg.pwr, bandOf(cfg.freq)) };
    const w = await this.writeLine(buildCfg(safe), 'macro');
    // Same rule as the remote path: remember only what the board was actually told. The cost is
    // that a failed APPLY leaves the sheet showing the previous values rather than the operator's
    // picks — which is the honest reading, because the board is still at the previous values.
    if (w.ok) this.rememberCfg(safe);
  }

  /**
   * Advance the stored config, keeping exactly one previous state — mirroring the firmware's one
   * saved `cfg_t` (06 §12.10). Every write to `this.cfg` goes through here, which is what keeps
   * the Config sheet honest after a command the operator did not type.
   */
  private rememberCfg(next: CfgState): void {
    // A config identical to the current one must NOT consume the undo slot. Sliding prevCfg
    // forward onto the same state turns UNDO into a no-op, and the commands that trigger it are
    // ordinary ones: `cfg pwr?` and `cfg?` are how the near side confirms a remote cfg landed,
    // and both merge to an unchanged state. buildCfg is the comparison because it is already the
    // canonical serialisation of exactly these six keys.
    if (buildCfg(next) === buildCfg(this.cfg)) return;
    this.prevCfg = this.cfg;
    this.cfg = next;
  }

  /**
   * Layer 2 of the fallback (06 §12.12): one button, no typing, for a helper who cannot be talked
   * through a console line over the phone.
   *
   * Independent of the firmware's `hold=` timer, and deliberately ignorant of it — `hold=` reverts
   * the far board on its own deadline whether the phone is reachable or not. Both may fire and
   * both revert to the same configuration, so the overlap is harmless. The re-sent line carries no
   * `hold=`: an undo is now, not later.
   */
  async undoLastConfig(): Promise<void> {
    if (this.prevCfg) await this.applyConfig(this.prevCfg);
  }

  async setTofdelay(value: number): Promise<void> {
    this.tofdelay = value;
    await this.send(setDelayCommand(String(value)), 'macro');
  }

  /** The session power ceiling — its own action, never folded into APPLY (07 §9.1). */
  async setPwrmax(value: number): Promise<void> {
    this.pwrmax = value;
    await this.send(setPwrmaxCommand(String(value)), 'macro');
  }

  /** GPS switch: gate whether fixes are written to the log (the display still updates). */
  setGpsLogging(enabled: boolean): void {
    this.gpsEnabled = enabled;
  }

  async start(): Promise<void> {
    this.p.onDisconnect(() => this.onLost());
    // A screen wake lock auto-releases when the page is hidden and is not re-acquired for us.
    // Without this, the display sleeps for the rest of a field session the first time the app
    // is backgrounded or the screen briefly locks — so re-take it on every return to front.
    this.p.onVisible(() => void this.reacquireWake());
    this.startGps();
    const prior = await this.p.store.sessions();
    if (prior.length > 0) {
      this.view.appendRow('evt', `${prior.length} earlier session(s) stored — open Sessions to download or delete`);
    }
  }

  /** Await the current read loop (test helper; resolves immediately when idle). */
  drain(): Promise<void> {
    return this.reading;
  }

  /** Refresh the displayed GPS fix age (driven by a 1 s ticker in main.ts). */
  tickGpsAge(): void {
    if (this.lastFixMono != null) this.view.setGps(this.lastAcc, this.p.clock.mono() - this.lastFixMono);
  }

  setMode(m: Mode): void {
    this.mode = m;
    this.view.setMode(m);
    if (m === 'graph') this.view.updateGraph(this.telemetry.stats(), this.telemetry.series());
  }

  async connect(): Promise<void> {
    const device = await this.p.requestDevice();
    if (!device) return; // cancelled or unavailable
    this.view.setConnection('connecting');
    const opened = await this.p.openPort(device);
    if (!opened.ok) {
      this.view.setConnection('disconnected');
      const at = describe(opened.error);
      this.view.appendRow('evt', `connect failed at ${at}`);
      // Only log the failure once the session has started; a never-started session
      // stays record-free rather than holding a lone err with no meta.
      if (this.started) await this.record(this.recorder.err('usb', `connect: ${at}`));
      return;
    }
    this.port = opened.value;
    this.view.setConnection('connected');
    this.wake = await this.p.acquireWakeLock();
    if (!this.started) {
      this.started = true;
      const dev: DeviceInfo = { vid: device.vendorId, pid: device.productId, ver: this.port.version };
      await this.record(this.recorder.meta(dev, BAUD)); // the session's first record (seq 0)
    }
    this.reading = this.readLoop();
  }

  private async readLoop(): Promise<void> {
    if (!this.port) return;
    for await (const chunk of this.port.read()) {
      for (const line of this.assembler.push(chunk)) {
        this.view.appendRow('rx', line);
        this.telemetry.push(line);
        if (this.mode === 'graph') this.view.updateGraph(this.telemetry.stats(), this.telemetry.series());
        // The log FIRST — it is the record of record (01 §4). Then the copy, which cannot throw,
        // cannot block, and so cannot delay, reorder or endanger anything on the way to the store.
        await this.record(this.recorder.line(line));
        try {
          this.p.upstream.out(line);
        } catch {
          // `createOutbox` never throws, so this is unreachable with the real sink — but
          // `upstream` is an injected dependency, and the read loop must not have to TRUST it.
          // Without the guard one throw ends the loop and truncates the log from that line on:
          // the relay, which is explicitly never load-bearing (REQ-REM-9), would have cost the
          // deliverable. Swallowed rather than surfaced because there is nothing the operator
          // could do about it and one bad sink would otherwise print per line.
        }
      }
    }
    this.onLost();
  }

  private onLost(): void {
    if (!this.port) return;
    this.port = null;
    this.view.setConnection('lost');
    void this.releaseWake();
    void this.record(this.recorder.err('usb', 'device disconnected'));
  }

  async send(text: string, src: 'macro' | 'typed'): Promise<void> {
    await this.writeLine(text, src);
  }

  /**
   * **Every remote command enters here and nowhere else** (REQ-REM-4, REQ-REM-5). One entry point
   * is auditable; two are not, and the guards below have to be un-bypassable by construction
   * rather than by everyone remembering to call them.
   *
   * The page guards **safety only** and does not re-implement the command grammar (02 §7). Two
   * copies of one grammar is exactly the drift REQ-CON-9 exists to prevent, arriving from the
   * other direction: a stricter copy would refuse a legitimate command two kilometres away with
   * nobody able to tell whether the board or the page said no, and a looser one adds nothing.
   * The board validates strictly and answers `err <cmd> <key>`, and that answer travels back to
   * the laptop over this same channel.
   */
  async applyRemote(line: string): Promise<RemoteOutcome> {
    const verb = line.split(' ')[0] ?? '';
    if (!REMOTE_VERBS.has(verb)) return await this.refuse('verb', `refused remote verb: ${verb}`);
    if (new TextEncoder().encode(line).length > LINE_CAP) {
      return await this.refuse('long', `refused remote line over ${LINE_CAP} bytes`);
    }

    let text = line;
    let next: CfgState | null = null;
    if (verb === 'cfg') {
      // The band comes from the line's own `freq=` when it carries one, so
      // `cfg freq=2450000000 pwr=20` clamps against 2.4 GHz and not against wherever the page
      // happens to be. The merge is computed from what will ACTUALLY be sent, which is what stops
      // the operator's next APPLY silently reverting it (07 §9.2).
      text = clampPwrInLine(line, mergeCfgLine(this.cfg, line).freq);
      next = mergeCfgLine(this.cfg, text);
    }
    const w = await this.writeLine(text, 'remote');
    if (!w.ok) return { kind: 'fail', reason: w.error };
    // Only AFTER the write. `this.cfg` models what the board is at, so a write that failed must
    // move nothing — otherwise the page believes a command landed that never did, and
    // `rememberCfg` would additionally walk `prevCfg` forward, leaving UNDO pointing at a
    // configuration the board has never been in.
    if (next) this.rememberCfg(next);
    return text === line ? { kind: 'sent' } : { kind: 'sent', changed: text };
  }

  /**
   * A refusal wrote no bytes, so it is not a USB failure — it is the page's own decision, and it
   * belongs in the file that comes home rather than only in the laptop's copy (REQ-REM-6).
   */
  private async refuse(reason: 'verb' | 'long', message: string): Promise<RemoteOutcome> {
    this.view.appendRow('evt', message);
    // AWAITED, like every other record. REQ-LOG-3 wants each record persisted before the
    // operation that produced it is considered complete, and the caller reports this outcome to
    // the laptop immediately — so a fire-and-forget write would race a laptop that has already
    // moved on, and lose the refusal if the tab died in between.
    if (this.started) await this.record(this.recorder.err('page', message));
    return { kind: 'fail', reason };
  }

  /**
   * The single writer. Every byte that reaches the board passes through here, and so does every
   * byte that fails to — which is the point (REQ-REM-6).
   *
   * Before this existed, `send` returned silently with no port and a failed write appended a view
   * row with no log record. Both are the same field failure: the operator is sure they sent
   * something, the board never heard it, and the file that comes home says nothing either way. A
   * `tx` record now means it went out; an `err` record means it did not.
   *
   * The `this.started` guard follows the existing convention: a session that never connected stays
   * record-free rather than holding a lone `err` ahead of `meta` (log-format §3).
   */
  private async writeLine(
    text: string,
    src: 'macro' | 'typed' | 'remote',
  ): Promise<Result<void, 'noport' | 'write' | 'stalled'>> {
    if (!this.port) {
      this.view.appendRow('evt', `not sent (no board): ${text}`);
      if (this.started) await this.record(this.recorder.err('usb', `send: no port: ${text}`));
      return err('noport');
    }
    // LEADING newline as well as trailing, and it is not cosmetic. The board's console picks up
    // stray bytes it was never sent -- 13 identical bytes about every 7 s, measured over a 4 h
    // soak (fw plan 10, E41 and Task 9). A command with junk in front of it is rejected and
    // silently lost. The leading LF closes that junk off as its own line, and PROTOCOL.md §1.2
    // makes an empty line explicitly ignored, so it costs one byte and nothing else.
    //
    // Every other writer to this UART already does it -- host/evb/transport.py:113 and the HIL
    // harness. This page did not, which left the FAR board, in a field, on the unprefixed path:
    // ~2 % of commands lost against 0.0042 % measured with the prefix. Do not "simplify" it away.
    //
    // The port owns the link, so there is no try/catch here any more: a halted endpoint or a
    // transfer that never completes comes back as a value, already recovered-or-not (REQ-USB-9).
    const w = await this.port.write(new TextEncoder().encode('\n' + text + '\n'));
    if (!w.ok) {
      // `stalled` is deliberately distinct from `write`. Both mean the board did not get it, but
      // only `stalled` says the page noticed its own link was wedged and tried to clear it — the
      // difference between "retry this" and "the device is gone" for whoever reads it later.
      const stalled = w.error === 'stalled';
      const detail = stalled ? 'link stalled, endpoint recovery failed' : 'write failed';
      this.view.appendRow('evt', `send failed: ${text}`);
      if (this.started) await this.record(this.recorder.err('usb', `send: ${detail}: ${text}`));
      return err(stalled ? 'stalled' : 'write');
    }
    this.view.appendRow('tx', text);
    await this.record(this.recorder.tx(text, src));
    return ok(undefined);
  }

  async openSessions(): Promise<void> {
    const sessions = await this.p.store.sessions();
    this.view.openSessions(sessions, this.session);
  }

  async download(session: string): Promise<void> {
    const parts: string[] = [];
    for await (const rec of this.p.store.read(session)) parts.push(toNdjsonLine(rec));
    this.p.save(parts, downloadName(session, this.p.now()));
  }

  private startGps(): void {
    this.p.watchGps(
      (pos) => {
        this.lastFixMono = this.p.clock.mono();
        this.lastAcc = pos.coords.accuracy;
        this.view.setGps(pos.coords.accuracy, 0);
        // The GPS switch gates logging, not the display — the operator still sees the fix.
        // Fixes log only after the session starts (meta written), so pre-connect fixes
        // don't consume seq 0. Gate before creating the record so the seq isn't advanced.
        if (this.started && this.gpsEnabled) void this.record(this.recorder.fix(pos));
      },
      () => this.view.setGps(null, null),
    );
  }

  private async record(rec: LogRecord): Promise<void> {
    this.count++;
    this.view.setRecordCount(this.count);
    try {
      await this.p.store.append(this.session, rec);
    } catch (e) {
      this.view.appendRow('evt', `storage error: ${(e as Error).message} — download soon`);
    }
  }

  private async releaseWake(): Promise<void> {
    try {
      await this.wake?.release();
    } catch {
      /* already gone */
    }
    this.wake = null;
  }

  /** Re-take the screen wake lock after returning to the foreground, if a session is live. */
  private async reacquireWake(): Promise<void> {
    if (!this.port) return; // only hold the lock while connected
    await this.releaseWake(); // drop the auto-released sentinel, then take a fresh one
    this.wake = await this.p.acquireWakeLock();
  }
}

/** The default transport opener — real openCh340 at the console baud. */
export const openPortDefault = (device: USBDevice): Promise<Result<Ch340Port, Ch340Error>> =>
  openCh340(device, BAUD);

function describe(e: Ch340Error): string {
  return e.kind === 'init' ? `${e.kind}/${e.step}` : e.kind;
}
